import { LitElement, html, css } from 'lit'
import { routes } from './router/routes.js'
import { geotagIcon, calendarIcon, megaphoneIcon, settingsIcon, houseFillIcon } from './styles/icons/object-icons.js'
import { chatIconFilled, calendarIconFilled } from './styles/icons/action-icons.js'
import { isFeatureEnabled } from './feature-flags.js'
import AppEvents from './app-events.js'
import { sessionUserIsAdmin } from './session/session.js'

class MobileNavBarElement extends LitElement {
  static properties = {
    currentPath: { type: String },
  }

  constructor() {
    super()
    this.navOpen = false
    this.expanded = false
    this.currentPath = window.location.pathname
    
    // Determine home route based on user role
    const homeRoute = sessionUserIsAdmin() ? routes.ADMIN.path : routes.DASHBOARD.path
    
    this.menuOptions = [
      {
        name: 'Home',
        icon: houseFillIcon,
        path: homeRoute,
      },
      {
        name: 'Map',
        icon: geotagIcon,
        path: routes.MAP.path || routes.RESOURCES.path,
      },
      {
        name: 'Announcements',
        icon: megaphoneIcon,
        path: routes.ANNOUNCEMENTS.path,
      },
      {
        name: 'Chat',
        icon: chatIconFilled,
        path: routes.CHAT.path,
      },
      {
        name: 'Calendar',
        icon: calendarIconFilled,
        path: routes.CALENDAR.path,
      },
      {
        name: 'Settings',
        icon: settingsIcon,
        path: routes.SETTINGS.path,
      },
    ]

    // Store bound function to properly remove event listeners
    this.boundHandleRouteChange = this.handleRouteChange.bind(this)
  }

  connectedCallback() {
    super.connectedCallback()
    // Listen for route changes
    window.addEventListener(AppEvents.ROUTE_CHANGE, this.boundHandleRouteChange)
    // Also listen for browser navigation events
    window.addEventListener('popstate', this.boundHandleRouteChange)
  }

  disconnectedCallback() {
    super.disconnectedCallback()
    window.removeEventListener(AppEvents.ROUTE_CHANGE, this.boundHandleRouteChange)
    window.removeEventListener('popstate', this.boundHandleRouteChange)
  }

  handleRouteChange() {
    this.currentPath = window.location.pathname
    this.requestUpdate()
  }

  isActiveRoute(optionPath) {
    const currentPath = this.currentPath
    
    // Exact match
    if (currentPath === optionPath) return true
    
    // Check if current path starts with option path (for nested routes)
    // But make sure it's not a partial match (e.g., /settings shouldn't match /set)
    if (currentPath.startsWith(optionPath + '/')) return true
    
    // Special cases for specific routes
    // Map/Resources are often the default/home route
    if (optionPath === routes.MAP.path && currentPath === '/') return true
    
    return false
  }

  render() {
    return html`
      <div class="main">
        <div class="nav">
          ${this.menuOptions.map(
            (option) => {
              const isActive = this.isActiveRoute(option.path)
              return html`
                <a
                  class="nav-item ${isActive ? 'active' : ''}"
                  href=${option.path}
                  aria-label=${option.name}
                  aria-current=${isActive ? 'page' : 'false'}
                >
                  <span class="icon-wrapper">
                    ${option.icon}
                  </span>
                  ${isActive ? html`<span class="active-indicator"></span>` : ''}
                </a>
              `
            }
          )}
        </div>
      </div>
    `
  }

  static styles = css`
    :host {
      --nav-button-right: 30px;
      --nav-button-bottom: 45px;
      --nav-button-width: 70px;
      --nav-button-radius: 70px;
    }
    @media (min-width: 601px) {
      .nav {
        position: static;
        height: auto;
        border-top: none;
        padding: 8px 16px;
      }
    }
    .nav {
      position: fixed;
      bottom: 0;
      left: 0;
      width: 100%;
      height: var(--nav-bar-height, 64px);
      background-color: var(--app-white);
      color: white;
      display: flex;
      flex-direction: row;
      justify-content: space-evenly;
      align-items: center;
      border-top: 2px solid lightgrey;
      cursor: pointer;
      z-index: 9999;
    }
    .main {
      display: flex;
      justify-content: center;
      align-items: center;
    }

    .nav-item {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 8px;
      cursor: pointer;
      text-decoration: none;
      transition: all 0.2s ease;
      border-radius: 8px;
    }

    .nav-item:active {
      transform: scale(0.95);
    }

    .icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
    }

    .nav-item svg {
      width: 25px;
      height: 25px;
      fill: #9ca3af;
      transition: all 0.2s ease;
    }

    .nav-item.active svg {
      fill: var(--app-primary-color, #6f2519);
      transform: translateY(-2px);
    }

    .nav-item:hover:not(.active) svg {
      fill: #6b7280;
    }

    .active-indicator {
      position: absolute;
      bottom: -4px;
      width: 4px;
      height: 4px;
      background: var(--app-primary-color, #6f2519);
      border-radius: 50%;
      animation: pulse 2s infinite;
    }

    @keyframes pulse {
      0%, 100% {
        opacity: 1;
        transform: scale(1);
      }
      50% {
        opacity: 0.7;
        transform: scale(1.2);
      }
    }

    .expanded {
      visibility: visible;
      width: 70%;
      height: calc(var(--nav-button-width) + 6px);
      padding-right: var(--nav-button-width);
      background-color: var(--app-white);
      border-radius: var(--nav-button-radius);
      border: 1px solid #e0e0e0;
      box-shadow: 0 0 2px rgba(0, 0, 0, 0.2);
      transition: visibility 5s ease;
      transition: width 0.3s ease;
      z-index: 100;
    }
    .expanded > a {
      /** Delay showing a tags for a bit */
      visibility: visible;
      transition: visibility 0.3s ease 0.3s;
    }
  `
}

customElements.define('mobile-nav-bar', MobileNavBarElement)
